import type {MouseEvent} from 'react';
import {useCallback, useMemo} from 'react';
import {styled} from 'styled-components';

import {max} from '@repo/shared/lib/math_utils';
import {removeUndefined} from '@repo/shared/lib/type_utils';
import {NzbsuSearchSort} from '@repo/shared/models';

import {ButtonAsLink} from '@repo/shared-web/components/core/button';
import {showRawModal} from '@repo/shared-web/components/core/modal';
import {SvgIcon} from '@repo/shared-web/components/core/svg_icon';
import {chevronDownIcon} from '@repo/shared-web/components/icons/chevron_down_icon';
import {chevronUpIcon} from '@repo/shared-web/components/icons/chevron_up_icon';
import {downloadIcon} from '@repo/shared-web/components/icons/download_icon';
import {infoIcon} from '@repo/shared-web/components/icons/info_icon';
import {EmptyFragment} from '@repo/shared-web/lib/react';

import {NzbCheck} from '@repo/website-frontend/components/nzb_check';
import {NzbTrackLanguages} from '@repo/website-frontend/components/nzb_track_languages';
import {apiCall} from '@repo/website-frontend/lib/api';
import {formatBytes} from '@repo/website-frontend/lib/bytes';
import {formatDate} from '@repo/website-frontend/lib/date';
import {getCheckingQueue, queueCheck} from '@repo/website-frontend/stores/checking_queue_store';
import {
  setNzbDownloadLink,
  useAllNzbDownloadLink,
} from '@repo/website-frontend/stores/nzb_download_link_store';
import {getNzbsu, useAllNzbsu} from '@repo/website-frontend/stores/nzbsu_store';

interface NzbTableProps {
  ids?: string[];
  sort: NzbsuSearchSort;
  onSortChange: (sortType: NzbsuSearchSort) => void;
  total?: number;
  durationSeconds?: number;
}

export const NzbTable: React.FC<NzbTableProps> = props => {
  const {ids, sort, onSortChange, total, durationSeconds} = props;
  const allNzbsu = useAllNzbsu();
  const downloadLinks = useAllNzbDownloadLink();

  const nzbItems = useMemo(
    () => (ids ? removeUndefined(ids.map(id => allNzbsu.get(id))) : undefined),
    [allNzbsu, ids],
  );

  const maxSize = max((nzbItems ?? []).map(item => item.size)) ?? 0;
  const maxPubTs = max((nzbItems ?? []).map(item => item.pubTs)) ?? 0;
  const maxGrabs = max((nzbItems ?? []).map(item => item.grabs ?? 0)) ?? 0;

  const handleDownloadClick = useCallback(
    async (evt: MouseEvent<HTMLElement>) => {
      // Get the download URL
      const guid = evt.currentTarget.dataset.guid;
      if (guid === undefined) {
        return;
      }
      let link = downloadLinks.get(guid);

      // Fetch if not in store
      if (link === undefined) {
        const item = nzbItems?.find(item => item.guid === guid);
        const {url} = await apiCall('POST /get-nzb-link', {
          id: guid,
          fileName: `${item?.title ?? guid}.nzb`,
        });
        setNzbDownloadLink(guid, url);
        link = url;
      }

      // Start the download
      const element = document.createElement('a');
      element.setAttribute('href', link);
      element.setAttribute('download', `${guid}.nzb`);
      element.style.display = 'none';
      document.body.append(element);
      element.click();
      document.body.removeChild(element);
    },
    [downloadLinks, nzbItems],
  );

  const handleNfoClick = useCallback(async (evt: MouseEvent<HTMLElement>) => {
    // Get the download URL
    const guid = evt.currentTarget.dataset.guid;
    if (guid === undefined) {
      return;
    }

    const {nfo} = await apiCall('POST /get-nzb-nfo', {id: guid});
    showRawModal({
      mode: 'fade-center',
      children: (
        <NfoModal>
          <NfoPre>{nfo}</NfoPre>
        </NfoModal>
      ),
    });
  }, []);

  const handleSortClick = useCallback(
    (evt: MouseEvent<HTMLElement>) => {
      // Get the download URL
      const sort = evt.currentTarget.dataset.sort;
      if (sort === undefined) {
        return;
      }
      onSortChange(sort as NzbsuSearchSort);
    },
    [onSortChange],
  );

  const handleCheckAllClick = useCallback(() => {
    const idsToCheck = ids?.filter(id => {
      const itemCheck = getCheckingQueue(id);
      const item = getNzbsu(id);
      return !item?.lastCheck && (!itemCheck || itemCheck.stage === 'failed');
    });
    for (const id of idsToCheck ?? []) {
      queueCheck(id);
    }
  }, [ids]);

  return (
    <Wrapper>
      <TableHeader $hidden={ids === undefined}>
        <div>{`${ids?.length} / ${total}`}</div>
        <div>•</div>
        <ButtonAsLink onClick={handleCheckAllClick}>Check all</ButtonAsLink>
      </TableHeader>

      <TableScroll>
        <HeaderLine>
          <Name>Name</Name>
          <Nfo>Nfo</Nfo>
          <Size
            data-sort={
              sort === NzbsuSearchSort.SizeDesc ? NzbsuSearchSort.SizeAsc : NzbsuSearchSort.SizeDesc
            }
            onClick={handleSortClick}
            $sortable
          >
            <SortableCol>
              <div>Size</div>
              {sort === NzbsuSearchSort.SizeAsc ? (
                <SvgIcon icon={chevronUpIcon} color="#fff" size={12} />
              ) : sort === NzbsuSearchSort.SizeDesc ? (
                <SvgIcon icon={chevronDownIcon} color="#fff" size={12} />
              ) : (
                EmptyFragment
              )}
            </SortableCol>
          </Size>
          {durationSeconds !== undefined ? (
            <Size>
              <div>Kbps</div>
            </Size>
          ) : (
            <></>
          )}
          <PubTs
            data-sort={
              sort === NzbsuSearchSort.PostedDesc
                ? NzbsuSearchSort.PostedAsc
                : NzbsuSearchSort.PostedDesc
            }
            onClick={handleSortClick}
            $sortable
          >
            <SortableCol>
              <div>Date</div>
              {sort === NzbsuSearchSort.PostedAsc ? (
                <SvgIcon icon={chevronUpIcon} color="#fff" size={12} />
              ) : sort === NzbsuSearchSort.PostedDesc ? (
                <SvgIcon icon={chevronDownIcon} color="#fff" size={12} />
              ) : (
                EmptyFragment
              )}
            </SortableCol>
          </PubTs>
          <Grabs
            data-sort={
              sort === NzbsuSearchSort.StatsDesc
                ? NzbsuSearchSort.StatsAsc
                : NzbsuSearchSort.StatsDesc
            }
            onClick={handleSortClick}
            $sortable
          >
            <SortableCol>
              <div>Stats</div>
              {sort === NzbsuSearchSort.StatsAsc ? (
                <SvgIcon icon={chevronUpIcon} color="#fff" size={12} />
              ) : sort === NzbsuSearchSort.StatsDesc ? (
                <SvgIcon icon={chevronDownIcon} color="#fff" size={12} />
              ) : (
                EmptyFragment
              )}
            </SortableCol>
          </Grabs>
          <Languages>Audio</Languages>
          <Languages>Subtitles</Languages>
          <Check>Health</Check>
          <Download>NZB</Download>
        </HeaderLine>
        {nzbItems ? (
          nzbItems.map(item => (
            <Line key={item.guid}>
              <Name title={item.title}>{item.title}</Name>
              <Nfo>
                {item['nfo'] ? (
                  <DownloadButton data-guid={item.guid} onClickAsync={handleNfoClick}>
                    <SvgIcon icon={infoIcon} size={16} color="#db7b26" />
                  </DownloadButton>
                ) : (
                  <></>
                )}
              </Nfo>
              <Size $highlighted={item.size === maxSize}>
                <span>{formatBytes(item.size, 1)}</span>
              </Size>
              {durationSeconds !== undefined ? (
                <Size>
                  <span>{Math.round((8 * item.size) / durationSeconds / 1024)}</span>
                </Size>
              ) : (
                <></>
              )}
              <PubTs $highlighted={item.pubTs === maxPubTs}>
                <span>{formatDate(new Date(item.pubTs))}</span>
              </PubTs>
              <Grabs $highlighted={item.grabs === maxGrabs}>
                <span>{item.grabs ?? 0}</span>
              </Grabs>
              <Languages>
                <NzbTrackLanguages metadata={item.lastCheck?.mediaMetadata} kind="audioTracks" />
              </Languages>
              <Languages>
                <NzbTrackLanguages metadata={item.lastCheck?.mediaMetadata} kind="subtitleTracks" />
              </Languages>
              <Check>
                <NzbCheck guid={item.guid} />
              </Check>
              <Download>
                <DownloadButton data-guid={item.guid} onClickAsync={handleDownloadClick}>
                  <SvgIcon
                    icon={downloadIcon}
                    color={downloadLinks.has(item.guid) ? '#2e6f31' : '#db7b26'}
                    size={16}
                  />
                </DownloadButton>
              </Download>
            </Line>
          ))
        ) : (
          <Loading>Loading...</Loading>
        )}
      </TableScroll>
    </Wrapper>
  );
};
NzbTable.displayName = 'NzbTable';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 16px;
  width: 100%;
`;

const TableHeader = styled.div<{$hidden: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffffaa;
  gap: 8px;
  ${p => (p.$hidden ? 'visibility: hidden;' : false)}
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 180px;
  font-size: 32px;
  color: #ffffff66;
  background: #ffffff05;
  border-radius: 12px;
`;

const BaseLine = styled.div`
  display: flex;
  align-items: center;
  min-width: 900px;
  box-sizing: border-box;
  padding: 4px 8px;
  color: #ffffffdd;
  gap: 4px;
`;

const TableScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-x: auto;
`;

const Line = styled(BaseLine)`
  background: #ffffff10;
  border-radius: 4px;
  &:hover {
    background-color: #ffffff20;
  }
`;

const HeaderLine = styled(BaseLine)`
  font-weight: 600;
`;

const Name = styled.div`
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Col = styled.div<{$highlighted?: boolean; $sortable?: boolean}>`
  flex-shrink: 0;
  width: 70px;
  text-align: right;
  white-space: nowrap;
  ${p =>
    p.$highlighted &&
    `
    & > * {
      background: #00ff0055;
      padding: 1px 4px;
      border-radius: 8px;
    }`}
  ${p =>
    p.$sortable &&
    `
    cursor: pointer;
    border-radius: 4px;
    user-select: none;
    &:hover {
      background-color: #ffffff22;
    }
    `}
`;

const SortableCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  &:first-child {
    flex-grow: 1;
  }
  &:last-child {
    flex-shrink: 0;
  }
`;

const Nfo = styled(Col)`
  width: 32px;
`;
const Size = styled(Col)``;
const PubTs = styled(Col)``;
const Grabs = styled(Col)``;
const Languages = styled(Col)`
  text-align: left;
  width: 116px;
  margin-left: 8px;
`;
const Check = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  margin-left: 8px;
`;
const Download = styled(Col)`
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DownloadButton = styled(ButtonAsLink)`
  justify-content: center;
  width: 30px;
  height: 24px;
  border-radius: 8px;
  &:hover {
    background-color: #ffffff20;
  }
`;

const NfoModal = styled.div`
  background-color: #151921dd;
  color: #ffffffaa;
  max-width: calc(100vw - 64px);
  max-height: calc(100vh - 64px);
  padding: 32px;
  border-radius: 8px;
  overflow: auto;
`;
const NfoPre = styled.pre`
  font-family: monospace;
`;
